import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';

//create canvas
export const createCanvas = createAsyncThunk(
  'canvas/createCanvas',
  async (canvasData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/canvas', canvasData);
      localStorage.setItem('canvasData', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Fetch user's canvases
export const fetchUserCanvases = createAsyncThunk(
  'canvas/fetchUserCanvases',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/canvas');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateCanvas = createAsyncThunk(
  'canvas/updateCanvas',
  async (canvasData, { rejectWithValue }) => {
    try {
      const updatePayload = {};
      
      if (canvasData.name !== undefined) {
        updatePayload.name = canvasData.name;
      }
      
      if (canvasData.type !== undefined) {
        updatePayload.type = canvasData.type;
      }
      
      if (canvasData.currency !== undefined) {
        updatePayload.currency = canvasData.currency;
      }

      // Update to include all possible fields
      const response = await apiClient.patch(
        `/canvas/${canvasData.id}`,
        updatePayload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete Canvas
export const deleteCanvas = createAsyncThunk(
  'canvas/deleteCanvas',
  async (canvasId, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`/canvas/${canvasId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

//Share Canvas
export const shareCanvas = createAsyncThunk(
  'canvas/shareCanvas',
  async (canvasInviteData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `/canvas/${canvasInviteData.canvasId}/share`,
        canvasInviteData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Fetch Canvases Shared by User
export const fetchCanvasesSharedByMe = createAsyncThunk(
  'canvas/fetchCanvasesSharedByMe',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('canvas/shared/by-me');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch canvases shared by you');
    }
  }
);

//fetch canvas by id
export const fetchCanvasById = createAsyncThunk(
  'canvas/fetchCanvasById',
  async (canvasId, {rejectWithValue})=>{
    try{
      const response = await apiClient.get(`/canvas/${canvasId}`);
      return response.data;

    }catch(error){
      return rejectWithValue(error.response?.data?.message || error.message || error)
    }
  }
)

// Slice for canvas state management
const canvasSlice = createSlice({
  name: 'canvas',
  initialState: {
    canvas: null,
    canvases: [],
    loading: false,
    error: null,
    sharedByMe: [],
  },
  reducers: {
    resetCanvasError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createCanvas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCanvas.fulfilled, (state, action) => {
        state.loading = false;
        state.canvas = action.payload;
      })
      .addCase(createCanvas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserCanvases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserCanvases.fulfilled, (state, action) => {
        state.loading = false;
        state.canvases = action.payload || [];
        state.error = null;
      })
      .addCase(fetchUserCanvases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.canvases = [];
      })
      .addCase(updateCanvas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCanvas.fulfilled, (state, action) => {
        state.loading = false;
        if(state.canvases.owned) {
          const updated = action.payload;
          const index = state.canvases.owned.findIndex(c => c._id === updated._id);
          if(index !== -1) {
            state.canvases.owned[index] = updated;
          }
        }
      })
      .addCase(updateCanvas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCanvas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCanvas.fulfilled, (state, action) => {
        state.loading = false;
        if(state.canvases.owned) {
          state.canvases.owned = state.canvases.owned.filter(
            canvas => canvas._id !== action.meta.arg
          );
        }
      })
      .addCase(deleteCanvas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(shareCanvas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(shareCanvas.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(shareCanvas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCanvasesSharedByMe.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCanvasesSharedByMe.fulfilled, (state, action) => {
        state.loading = false;
        state.sharedByMe = action.payload;
      })
      .addCase(fetchCanvasesSharedByMe.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCanvasById.pending, (state, action)=>{
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCanvasById.rejected, (state, action)=>{
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCanvasById.fulfilled, (state, action)=>{
        state.loading = false;
        state.canvas = action.payload;
      })
  },
});

//Selectors
export const selectCanvases = (state) => state.canvas.canvases;
export const selectCanvasLoading = (state) => state.canvas.loading;
export const selectCanvasError = (state) => state.canvas.error;

export const { resetCanvasError } = canvasSlice.actions;
export default canvasSlice.reducer;
